import { Footer, Header, Page, PageHeader, PageTransition } from '@components/Page'
import { pickSeoProps } from '@components/Page/Page'
import StoresGrid, { GridItem, OpeningHours } from '@components/_stores/StoresGrid'
import { buildLanguageAlternateSlugItems } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { MultipleItemResponse, SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { getAllBrands } from '@lib/kontent/getAllBrands'
import { getAllStoreServices } from '@lib/kontent/getAllStoreServices'
import { BrandPage } from '@lib/kontent/models/BrandPage'
import { StorePage } from '@lib/kontent/models/StorePage'
import { StoreService } from '@lib/kontent/models/StoreService'
import { StoresPage as StoresPageModel } from '@lib/kontent/models/StoresPage'
import { add, endOfDay, getUnixTime, startOfDay } from 'date-fns'
import { format, getTimezoneOffset, toDate } from 'date-fns-tz'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import * as qs from 'query-string'

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

function getOpeninghours(store: StorePage) {
  let openingHours: OpeningHours = {}
  const timezone =
    store.country?.value[0]?.codename === 'singapore' ? '+08:00' : getHourOffsetEuropeanTime()
  days.forEach((day, index) => {
    if (store[day] && store[day].value.includes(':')) {
      const parts = store[day].value.split('-')

      let from = toDate(`${format(new Date(), 'yyyy-MM-dd')}T${parts[0]}:00${timezone}`)
      let till = toDate(`${format(new Date(), 'yyyy-MM-dd')}T${parts[1]}:00${timezone}`)
      if (parts[0] === '00:00') {
        from = startOfDay(from)
      }
      // Force to be at the end of the day, e.g. 23:59:59
      if (parts[1] === '00:00' || parts[1] === '24:00') {
        till = endOfDay(add(till, { days: 1 }))
      }

      openingHours[index] = {
        from: getUnixTime(from),
        till: getUnixTime(till),
      }
    } else {
      openingHours[index] = {
        closedText: store[day].value,
      }
    }
  })
  return openingHours
}

function getGridItems(stores: StorePage[]): GridItem[] {
  const today = parseInt(format(new Date(), 'i'), 10) - 1
  let items = []
  stores.forEach((store) => {
    const openingHours = getOpeninghours(store)
    const data = {
      urlSlug: store.url_slug.value,
      title: store.title.value,
      country: store.country.value[0]?.codename,
      address: store.short_adress.value,
      image: store.image.value[0]?.url || null,
      openingHours,
      openendTodayTill: openingHours[today]?.till ? openingHours[today].till : null,
      openingHoursOverwrite: store.overwrite_opening_hours.value,
      services: store.services.value,
      brands: store.brands.value,
    }

    items.push(data)
  })

  return items
}

const parseFilters = (query) => {
  let filters = {}
  Object.keys(query).forEach((key) => {
    filters[key] = query[key].split(',')
  })

  return filters
}

type Props = SingleItemResponse<StoresPageModel> & {
  brands?: MultipleItemResponse<BrandPage>
  services?: MultipleItemResponse<StoreService>
}
const StoresPage: NextPage<Props> = ({ item, modular_content, brands, services }) => {
  const router = useRouter()
  const query = router.asPath.includes('?') ? qs.parse(router.asPath.split('?').pop()) : {}
  const initialFilters = query ? parseFilters(query) : {}
  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateSlugItems(router.locales, 'stores')}
    >
      <Header initialVariant="absolute" />
      <PageTransition>
        <PageHeader
          title={item.elements.title.value}
          text={item.elements.text.value}
          pb={{ _: '2rem', medium: '3rem', large: '3.5rem' }}
        />
        <StoresGrid
          initialFilters={initialFilters}
          netherlands={getGridItems(
            item.elements.netherlands.value.map((key) => modular_content[key].elements),
          )}
          singapore={getGridItems(
            item.elements.singapore.value.map((key) => modular_content[key].elements),
          )}
          brands={brands}
          services={services}
        />
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default StoresPage

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const content = await getPageByCodename<StoresPageModel>({
    codename: 'stores_page',
    lang: locale,
    preview,
  })
  const brands = await getAllBrands({
    language: locale,
    preview,
  })
  const services = await getAllStoreServices({
    language: locale,
    preview,
  })
  return {
    props: {
      ...content,
      brands: brands as MultipleItemResponse<BrandPage>,
      services: services as MultipleItemResponse<StoreService>,
      ...(await getServerSideTranslations(locale)),
    },
  }
}

function getHourOffsetEuropeanTime(): string {
  const offset = getTimezoneOffset('Europe/Amsterdam') / 60 / 60 / 1000
  return `+0${offset}:00`
}
